import {Component, effect, OnDestroy, OnInit, Signal, AfterViewInit} from '@angular/core';

import {ReactiveFormsModule, UntypedFormBuilder, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {HttpErrorResponse, HttpStatusCode} from '@angular/common/http';

import {ProductService} from '../../services/product/product.service';
import {ContextService} from '../../services/context/context-signal.service';
import {PageHeaderService} from '../../services/page-header/page-header.service';
import {PageAction} from '../../components/app-shell/page-action.model';

import {Product} from '../../models/product';
import {Context} from '../../models/context';
import { AppLogger, AppLoggerService } from '../../core/logging';

import {
  UiButtonComponent,
  UiInputComponent,
  UiSelectComponent,
  UiSelectOption,
  UiButtonGroupComponent
} from '../../shared/ui';
import {TagsInputComponent} from '../../components/tags-input/tags-input.component';

@Component({
  selector: 'app-edit-product',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FontAwesomeModule,
    UiButtonComponent,
    UiInputComponent,
    UiSelectComponent,
    UiButtonGroupComponent,
    TagsInputComponent
],
  templateUrl: './edit-product.component.html',
  styleUrl: './edit-product.component.scss'
})
export class EditProductComponent implements OnInit, OnDestroy, AfterViewInit {

  private readonly destroy$ = new Subject<void>();

  private readonly log: AppLogger;

  context: Signal<Context> = this.contextService.context;

  productId: number;
  product: Product | null = null;
  householdId: number;
  isLoading = true;

  // Default expiry helpers
  private readonly DAYS_PER_WEEK = 7;
  private readonly DAYS_PER_MONTH = 30;
  private readonly DAYS_PER_YEAR = 365;

  productForm = this.formBuilder.group({
    name: ['', Validators.required],
    quantityType: ['fixed'],
    // Quantity value is a decimal representation and must be sent as string to the backend
    quantityValue: [''],
    quantityUnit: [''],
    brands: this.formBuilder.nonNullable.control([] as string[]),
    codes: this.formBuilder.nonNullable.control([] as string[]),
    defaultExpiryValue: [null],
    defaultExpiryUnit: ['days'],
  });

  error: string | null = null;
  isSaving = false;

  // Select options
  quantityUnitOptions: UiSelectOption[] = [
    {value: '', label: '-- select unit --'},
    {value: 'g', label: 'g'},
    {value: 'kg', label: 'kg'},
    {value: 'ml', label: 'ml'},
    {value: 'l', label: 'l'},
    {value: 'piece', label: 'piece'},
    {value: 'unknown', label: 'unknown'}
  ];


  constructor(
    private contextService: ContextService,
    private productService: ProductService,
    private formBuilder: UntypedFormBuilder,
    private appLogger: AppLoggerService,
    private router: Router,
    private route: ActivatedRoute,
    private pageHeaderService: PageHeaderService
  ) {
    this.log = this.appLogger.createForClass(EditProductComponent);

    effect(() => {
      const c = this.context();
      if (!c?.isSet?.()) return;
      this.householdId = c.household.id;
      if (this.productId) {
        this.loadProduct();
      }
    });
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.productId = Number(params['id']);
      if (this.householdId) {
        this.loadProduct();
      }
    });
    this.pageHeaderService.setPageTitle("Edit Product");

    // Re-publish actions whenever the form's validity changes (Save enabled state).
    this.productForm.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refreshPageActions());
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearAll();
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    this.refreshPageActions();
  }

  /**
   * Declarative toolbar actions:
   *  - Save: primary, pinned (always visible). Disabled while saving, while
   *    loading the product, or when the form is invalid. Shows a spinner
   *    while saving.
   *  - Cancel: default secondary action — inline on desktop, auto-collapses
   *    into the ⋯ overflow on mobile. Disabled while saving.
   */
  private refreshPageActions(): void {
    const actions: PageAction[] = [
      {
        type: 'button',
        id: 'cancel',
        label: 'Cancel',
        variant: 'neutral',
        pinned: true,
        disabled: this.isSaving,
        onClick: () => this.onCancel(),
      },
      {
        type: 'button',
        id: 'save',
        label: 'Save',
        variant: 'primary',
        pinned: true,
        disabled: !this.productForm.valid || this.isSaving || this.isLoading,
        loading: this.isSaving,
        onClick: () => this.onSave(),
      },
    ];
    this.pageHeaderService.setPageActions(actions);
  }

  loadProduct(): void {
    if (!this.householdId || !this.productId) {
      return;
    }

    this.isLoading = true;
    this.refreshPageActions();
    this.productService.getProduct(this.productId).subscribe({
      next: (product) => {
        this.product = product;
        this.populateForm(product);
        this.isLoading = false;
        this.refreshPageActions();
      },
      error: (err) => {
        this.log.error('Error loading product', err, { productId: this.productId });
        this.error = 'Failed to load product. Please try again.';
        this.isLoading = false;
        this.refreshPageActions();
      }
    });
  }

  setQuantityType(type: 'fixed' | 'variable'): void {
    this.productForm.patchValue({quantityType: type});
  }

  populateForm(product: Product): void {
    const {value: expiryValue, unit: expiryUnit} = this.daysToExpiryValueAndUnit(product.default_expiry_days);

    const normalizedQuantityUnit = this.normalizeUnit(product.quantity?.unit);

    this.productForm.patchValue({
      name: product.name,
      quantityType: product.quantity_type || 'fixed',
      quantityValue: product.quantity?.value ?? '',
      quantityUnit: normalizedQuantityUnit || '',
      brands: product.brands || [],
      codes: (product.codes || []).map(c => String(c.code || '')).map(c => c.trim()).filter(Boolean),
      defaultExpiryValue: expiryValue,
      defaultExpiryUnit: expiryUnit
    });

    this.log.debug('Form populated with product data', {
      rawQuantityUnit: product.quantity?.unit,
      normalizedQuantityUnit,
      formValue: this.productForm.value
    });
  }

  private daysToExpiryValueAndUnit(days: number | null | undefined): { value: number | null; unit: 'days' | 'weeks' | 'months' | 'years' } {
    if (!days || days <= 0) {
      return {value: null, unit: 'days'};
    }

    // Convert to the largest sensible unit
    if (days >= this.DAYS_PER_YEAR && days % this.DAYS_PER_YEAR === 0) {
      return {value: days / this.DAYS_PER_YEAR, unit: 'years'};
    }
    if (days >= this.DAYS_PER_MONTH && days % this.DAYS_PER_MONTH === 0) {
      return {value: days / this.DAYS_PER_MONTH, unit: 'months'};
    }
    if (days >= this.DAYS_PER_WEEK && days % this.DAYS_PER_WEEK === 0) {
      return {value: days / this.DAYS_PER_WEEK, unit: 'weeks'};
    }

    return {value: days, unit: 'days'};
  }

  setDefaultExpiryUnit(unit: 'days' | 'weeks' | 'months' | 'years'): void {
    this.productForm.patchValue({defaultExpiryUnit: unit});
  }

  /**
   * Normalizes a user-entered decimal string.
   * - trims whitespace
   * - converts comma decimal separator to dot
   * - returns null for empty/invalid values
   */
  normalizeDecimalString(value: any): string | null {
    const raw = String(value ?? '').trim();
    if (!raw) return null;

    const normalized = raw.replace(',', '.');
    // allow digits with optional single dot and fractional part (e.g. 1, 1., .5, 1.5)
    if (!/^(?:\d+\.?\d*|\.\d+)$/.test(normalized)) {
      return null;
    }

    // avoid sending trailing dot ("1." -> "1")
    return normalized.endsWith('.') ? normalized.slice(0, -1) : normalized;
  }

  parseIntValue(value: any): number | null {
    if (!value) return null;
    const num = parseInt(value, 10);
    return isNaN(num) ? null : num;
  }

  private normalizeUnit(unit?: string | null): string {
    return String(unit ?? '').trim().toLowerCase();
  }

  private detectUnitTypeFromUnit(unit?: string | null): string {
    const u = this.normalizeUnit(unit);

    if (u === 'g' || u === 'kg') {
      return 'weight';
    }
    if (u === 'ml' || u === 'l') {
      return 'volume';
    }
    if (u === 'piece') {
      return 'piece';
    }

    return '';
  }

  private clampNonNegativeInt(v: any): number {
    const n = Number(v);
    if (!isFinite(n) || isNaN(n)) {
      return 0;
    }
    return Math.max(0, Math.floor(n));
  }

  private expiryValueAndUnitToDays(value: any, unit: any): number {
    const v = this.clampNonNegativeInt(value);
    const u = String(unit || 'days').toLowerCase();

    if (v === 0) {
      return 0;
    }

    switch (u) {
      case 'years':
        return v * this.DAYS_PER_YEAR;
      case 'months':
        return v * this.DAYS_PER_MONTH;
      case 'weeks':
        return v * this.DAYS_PER_WEEK;
      case 'days':
      default:
        return v;
    }
  }

  onCancel(): void {
    this.router.navigate(['/products', this.productId]);
  }

  onSave(): void {
    if (!this.product) {
      return;
    }

    this.error = null;
    this.isSaving = true;
    this.refreshPageActions();

    const form = this.productForm.value;
    const defaultExpiryDays = this.expiryValueAndUnitToDays(
      form.defaultExpiryValue,
      form.defaultExpiryUnit
    );
    const derivedUnitType = this.detectUnitTypeFromUnit(form.quantityUnit);

    const quantityValue = this.normalizeDecimalString(form.quantityValue);

    const updateRequest = {
      name: form.name,
      quantity_type: (form.quantityType || 'fixed') as 'fixed' | 'variable',
      quantity: quantityValue ? {
        value: quantityValue,
        unit: form.quantityUnit,
        unit_type: derivedUnitType,
      } : undefined,
      brands: form.brands,
      codes: form.codes,
      default_expiry_days: defaultExpiryDays
    };

    this.productService.updateProduct(this.productId, updateRequest).subscribe({
      next: (resp) => {
        this.log.debug('Product updated successfully', { resp });
        this.isSaving = false;
        this.refreshPageActions();
        this.router.navigate(['/products', this.productId]);
      },
      error: (err: HttpErrorResponse) => {
        this.log.error('Error updating product', err);
        this.isSaving = false;
        this.refreshPageActions();

        if (err.status === HttpStatusCode.InternalServerError) {
          this.error = 'An internal server error occurred. Please try again later or contact support if the problem persists.';
        } else {
          this.error = `An error occurred while updating the product: ${err.message || 'Unknown error'}`;
        }
      }
    });
  }
}
